import { useEffect, useState, useRef, useCallback } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Plus, Pencil, Trash2, Search, Table2 } from "lucide-react";
import { toast } from "sonner";
import { FieldManager } from "./FieldManager";
import { RecordEditor } from "./RecordEditor";
import { FilterBuilder } from "./FilterBuilder";
import { SortBuilder } from "./SortBuilder";
import { FilterPresets } from "./FilterPresets";
import { InlineCellEditor } from "./InlineCellEditor";
import { LinkedRecordCell } from "./LinkedRecordCell";
import { AttachmentGallery } from "./AttachmentGallery";
import { TextSelectionSmartChip } from "@/components/ai/TextSelectionSmartChip";
import { CSVImportWizard } from "@/components/ai/CSVImportWizard";
import { useGridNavigation } from "@/hooks/useGridNavigation";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Input } from "@/components/ui/input";
import { cn } from "@/lib/utils";

interface GridViewProps {
  table: any;
}

export const GridView = ({ table }: GridViewProps) => {
  const { user } = useAuth();
  const [fields, setFields] = useState<any[]>([]);
  const [records, setRecords] = useState<any[]>([]);
  const [filteredRecords, setFilteredRecords] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedRecord, setSelectedRecord] = useState<any>(null);
  const [editingRecord, setEditingRecord] = useState(false);
  const [filters, setFilters] = useState<any[]>([]);
  const [sorts, setSorts] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [linkedRecordsCache, setLinkedRecordsCache] = useState<Record<string, string[]>>({});
  const [attachmentsCache, setAttachmentsCache] = useState<Record<string, any[]>>({});
  const containerRef = useRef<HTMLDivElement>(null);

  const handleCellSave = useCallback(async (recordId: string, fieldName: string, value: any) => {
    try {
      const record = records.find(r => r.id === recordId);
      if (!record) return;

      const newData = { ...record.data, [fieldName]: value };
      
      const { error } = await supabase
        .from("records")
        .update({ data: newData })
        .eq("id", recordId);

      if (error) throw error;

      setRecords(prev => 
        prev.map(r => r.id === recordId ? { ...r, data: newData } : r)
      );
    } catch (error: any) {
      toast.error("Failed to save");
      console.error(error);
    }
  }, [records]);

  const handleCellDelete = useCallback(async (position: { row: number; col: number }) => {
    const record = filteredRecords[position.row];
    const field = fields[position.col];
    if (record && field) {
      await handleCellSave(record.id, field.name, null);
    }
  }, [filteredRecords, fields, handleCellSave]);

  const {
    selectedCell,
    editingCell,
    isSelected,
    isEditingCell,
    selectCell,
    startEditing,
    stopEditing,
  } = useGridNavigation({
    rowCount: filteredRecords.length,
    colCount: fields.length,
    containerRef: containerRef as React.RefObject<HTMLElement>,
    onCellDelete: handleCellDelete,
    onCellSave: () => {
      // Save handled by InlineCellEditor
    },
  });

  useEffect(() => {
    if (table) {
      fetchFields();
      fetchRecords();
    }
  }, [table]);

  useEffect(() => {
    applyFiltersAndSort();
  }, [records, filters, sorts, searchQuery]);

  useEffect(() => {
    if (fields.length > 0 && records.length > 0) {
      fetchLinkedRecords();
      fetchAttachments();
    }
  }, [fields, records]);

  const applyFiltersAndSort = () => {
    let filtered = [...records];

    // Apply search
    if (searchQuery) {
      filtered = filtered.filter((record) =>
        Object.values(record.data || {}).some((value) =>
          String(value).toLowerCase().includes(searchQuery.toLowerCase())
        )
      );
    }

    // Apply filters
    filters.forEach((filter) => {
      filtered = filtered.filter((record) => {
        const value = record.data?.[filter.field];
        const filterValue = filter.value;

        switch (filter.operator) {
          case "equals":
            return String(value) === String(filterValue);
          case "contains":
            return String(value).toLowerCase().includes(String(filterValue).toLowerCase());
          case "startsWith":
            return String(value).toLowerCase().startsWith(String(filterValue).toLowerCase());
          case "endsWith":
            return String(value).toLowerCase().endsWith(String(filterValue).toLowerCase());
          case "greaterThan":
            return Number(value) > Number(filterValue);
          case "lessThan":
            return Number(value) < Number(filterValue);
          case "isEmpty":
            return !value;
          case "isNotEmpty":
            return !!value;
          default:
            return true;
        }
      });
    });

    // Apply sorting
    if (sorts.length > 0) {
      filtered.sort((a, b) => {
        for (const sort of sorts) {
          const aVal = a.data?.[sort.field];
          const bVal = b.data?.[sort.field];
          
          if (aVal === bVal) continue;
          
          const comparison = aVal < bVal ? -1 : 1;
          return sort.direction === "asc" ? comparison : -comparison;
        }
        return 0;
      });
    }

    setFilteredRecords(filtered);
  };

  const fetchFields = async () => {
    try {
      const { data, error } = await supabase
        .from("fields")
        .select("*")
        .eq("table_id", table.id)
        .order("order_index", { ascending: true });

      if (error) throw error;
      setFields(data || []);
    } catch (error: any) {
      toast.error("Failed to load fields");
    }
  };

  const fetchRecords = async () => {
    try {
      const { data, error } = await supabase
        .from("records")
        .select("*")
        .eq("table_id", table.id)
        .order("created_at", { ascending: false });

      if (error) throw error;
      setRecords(data || []);
    } catch (error: any) {
      toast.error("Failed to load records");
    } finally {
      setLoading(false);
    }
  };

  const fetchLinkedRecords = async () => {
    const linkFields = fields.filter(f => f.type === 'link_to_record');
    if (linkFields.length === 0) return;

    const cache: Record<string, string[]> = {};
    
    for (const record of records) {
      for (const field of linkFields) {
        const key = `${record.id}-${field.id}`;
        
        // Fetch linked record IDs from record_links table
        const { data } = await supabase
          .from("record_links")
          .select("target_record_id")
          .eq("source_record_id", record.id)
          .eq("field_id", field.id);
        
        cache[key] = (data || []).map(d => d.target_record_id);
      }
    }
    
    setLinkedRecordsCache(cache);
  };

  const fetchAttachments = async () => {
    const attachmentFields = fields.filter(f => f.type === 'attachment');
    if (attachmentFields.length === 0) return;

    const cache: Record<string, any[]> = {};
    
    for (const record of records) {
      for (const field of attachmentFields) {
        const key = `${record.id}-${field.id}`;
        
        const { data } = await supabase
          .from("attachments")
          .select("*")
          .eq("record_id", record.id)
          .eq("field_id", field.id);
        
        cache[key] = data || [];
      }
    }
    
    setAttachmentsCache(cache);
  };

  const createNewRecord = async () => {
    try {
      const { data, error } = await supabase
        .from("records")
        .insert([{
          table_id: table.id,
          data: {},
          created_by: user?.id
        }])
        .select()
        .single();

      if (error) throw error;
      setRecords([data, ...records]);
      setSelectedRecord(data);
      setEditingRecord(true);
    } catch (error: any) {
      toast.error("Failed to create record");
    }
  };

  const deleteRecord = async (recordId: string) => {
    try {
      const { error } = await supabase
        .from("records")
        .delete()
        .eq("id", recordId);

      if (error) throw error;
      setRecords(records.filter(r => r.id !== recordId));
      toast.success("Record deleted");
    } catch (error: any) {
      toast.error("Failed to delete record");
    }
  };

  const handleLinkedRecordChange = async (recordId: string, fieldId: string, newLinkedIds: string[]) => {
    try {
      // Delete existing links
      await supabase
        .from("record_links")
        .delete()
        .eq("source_record_id", recordId)
        .eq("field_id", fieldId);

      // Insert new links
      if (newLinkedIds.length > 0) {
        const links = newLinkedIds.map(targetId => ({
          source_record_id: recordId,
          target_record_id: targetId,
          field_id: fieldId,
        }));

        const { error } = await supabase
          .from("record_links")
          .insert(links);

        if (error) throw error;
      }

      // Update cache
      setLinkedRecordsCache(prev => ({
        ...prev,
        [`${recordId}-${fieldId}`]: newLinkedIds,
      }));

      toast.success("Links updated");
    } catch (error: any) {
      console.error(error);
      toast.error("Failed to update links");
    }
  };

  const renderCell = (record: any, field: any, rowIndex: number, colIndex: number) => {
    const value = record.data?.[field.name];
    const cellKey = `${record.id}-${field.id}`;
    const isCellSelected = isSelected(rowIndex, colIndex);
    const isCellEditing = isEditingCell(rowIndex, colIndex);

    // Handle special field types
    if (field.type === 'link_to_record') {
      const targetTableId = field.config?.target_table_id;
      const linkedIds = linkedRecordsCache[cellKey] || [];
      
      return (
        <LinkedRecordCell
          fieldId={field.id}
          sourceRecordId={record.id}
          targetTableId={targetTableId}
          linkedRecordIds={linkedIds}
          onLinksChange={(ids) => handleLinkedRecordChange(record.id, field.id, ids)}
          isSelected={isCellSelected}
          allowMultiple={field.config?.allow_multiple !== false}
        />
      );
    }

    if (field.type === 'attachment') {
      const attachments = attachmentsCache[cellKey] || [];
      return (
        <AttachmentGallery
          attachments={attachments}
          compact
          readOnly={false}
        />
      );
    }

    // Use inline cell editor for other types
    return (
      <InlineCellEditor
        field={field}
        value={value}
        isEditing={isCellEditing}
        isSelected={isCellSelected}
        onStartEdit={() => selectCell({ row: rowIndex, col: colIndex }, true)}
        onSave={(newValue) => {
          handleCellSave(record.id, field.name, newValue);
          stopEditing(true);
        }}
        onCancel={() => stopEditing(false)}
      />
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-pulse text-muted-foreground">Loading...</div>
      </div>
    );
  }

  return (
    <div ref={containerRef} className="h-full flex flex-col relative" tabIndex={0}>
      <TextSelectionSmartChip
        tableSchema={{
          fields: fields.map(f => ({
            name: f.name,
            type: f.type,
            config: f.config
          }))
        }}
        onEntitiesExtracted={async (entities) => {
          for (const entity of entities) {
            await supabase.from("records").insert([{
              table_id: table.id,
              data: entity,
              created_by: user!.id
            }]);
          }
          toast.success(`Added ${entities.length} record(s) from text`);
          fetchRecords();
        }}
      />

      {/* Toolbar */}
      <div className="flex items-center justify-between p-3 md:p-4 border-b border-border bg-card gap-2 flex-wrap">
        <div className="flex items-center gap-2 flex-wrap flex-1">
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 w-40 md:w-56 h-9"
            />
          </div>
          <FilterBuilder
            fields={fields}
            currentFilters={filters}
            onApplyFilters={setFilters}
          />
          <SortBuilder
            fields={fields}
            currentSort={sorts}
            onApplySort={setSorts}
          />
          <div className="hidden md:block">
            <FilterPresets
              filters={filters}
              sorts={sorts}
              onApplyPreset={(newFilters, newSorts) => {
                setFilters(newFilters);
                setSorts(newSorts);
              }}
            />
          </div>
          <div className="hidden md:block">
            <CSVImportWizard 
              tableId={table.id} 
              existingFields={fields} 
              onImportComplete={fetchRecords} 
            />
          </div>
        </div>
        <div className="flex items-center gap-2">
          <FieldManager tableId={table.id} onFieldCreated={fetchFields} />
          <Button size="sm" onClick={createNewRecord} className="gap-1.5">
            <Plus className="h-4 w-4" />
            <span className="hidden sm:inline">New Record</span>
          </Button>
        </div>
      </div>

      {/* Grid */}
      <div className="flex-1 overflow-auto">
        {fields.length === 0 ? (
          <div className="flex items-center justify-center h-full p-8">
            <div className="text-center max-w-md">
              <div className="h-16 w-16 rounded-2xl bg-primary/10 flex items-center justify-center mx-auto mb-4">
                <Table2 className="h-8 w-8 text-primary" />
              </div>
              <h3 className="text-lg font-semibold mb-2">No fields yet</h3>
              <p className="text-muted-foreground text-sm mb-4">
                Add fields to define the structure of your table
              </p>
              <FieldManager tableId={table.id} onFieldCreated={fetchFields}>
                <Button className="gap-2">
                  <Plus className="h-4 w-4" />
                  Add Field
                </Button>
              </FieldManager>
            </div>
          </div>
        ) : (
          <table className="w-full border-collapse">
            <thead className="sticky top-0 bg-muted/80 backdrop-blur-sm z-10">
              <tr>
                <th className="border-b border-r border-border p-2.5 w-12 text-center text-xs font-medium text-muted-foreground">#</th>
                {fields.map((field) => (
                  <th key={field.id} className="border-b border-r border-border p-2.5 text-left min-w-[150px]">
                    <div className="font-medium text-sm">{field.name}</div>
                    <div className="text-xs text-muted-foreground capitalize">{field.type.replace("_", " ")}</div>
                  </th>
                ))}
                <th className="border-b border-border p-2.5 w-20 text-center text-xs font-medium text-muted-foreground">Actions</th>
              </tr>
            </thead>
            <tbody>
              {filteredRecords.length === 0 ? (
                <tr>
                  <td colSpan={fields.length + 2} className="border-b border-border p-12 text-center">
                    <div className="text-muted-foreground">
                      {records.length === 0 ? (
                        <div>
                          <p className="mb-2">No records yet</p>
                          <Button variant="outline" size="sm" onClick={createNewRecord} className="gap-2">
                            <Plus className="h-4 w-4" />
                            Add your first record
                          </Button>
                        </div>
                      ) : (
                        "No records match your filters"
                      )}
                    </div>
                  </td>
                </tr>
              ) : (
                filteredRecords.map((record, rowIndex) => (
                  <tr key={record.id} className="group hover:bg-muted/50 transition-colors">
                    <td className="border-b border-r border-border p-2.5 text-center text-xs text-muted-foreground">
                      {rowIndex + 1}
                    </td>
                    {fields.map((field, colIndex) => (
                      <td
                        key={field.id}
                        className={cn(
                          "border-b border-r border-border p-0 text-sm",
                          isSelected(rowIndex, colIndex) && "bg-primary/5"
                        )}
                        onClick={() => {
                          if (field.type !== 'link_to_record' && field.type !== 'attachment') {
                            selectCell({ row: rowIndex, col: colIndex });
                          }
                        }}
                        onDoubleClick={() => {
                          if (field.type !== 'formula' && field.type !== 'link_to_record' && field.type !== 'attachment') {
                            selectCell({ row: rowIndex, col: colIndex }, true);
                          }
                        }}
                      >
                        {renderCell(record, field, rowIndex, colIndex)}
                      </td>
                    ))}
                    <td className="border-b border-border p-2.5">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="sm" className="h-7 w-7 p-0 opacity-0 group-hover:opacity-100 transition-opacity">
                            •••
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end">
                          <DropdownMenuItem
                            onClick={() => {
                              setSelectedRecord(record);
                              setEditingRecord(true);
                            }}
                          >
                            <Pencil className="h-4 w-4 mr-2" />
                            Edit
                          </DropdownMenuItem>
                          <DropdownMenuItem
                            className="text-destructive focus:text-destructive"
                            onClick={() => deleteRecord(record.id)}
                          >
                            <Trash2 className="h-4 w-4 mr-2" />
                            Delete
                          </DropdownMenuItem>
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        )}
      </div>

      {/* Record Editor Dialog */}
      {selectedRecord && (
        <RecordEditor
          record={selectedRecord}
          fields={fields}
          open={editingRecord}
          onClose={() => {
            setEditingRecord(false);
            setSelectedRecord(null);
          }}
          onSave={() => {
            fetchRecords();
            setEditingRecord(false);
            setSelectedRecord(null);
          }}
        />
      )}
    </div>
  );
};
